import { create } from 'zustand';
import type { OnboardingState, OnboardingStep, SpotlightPosition } from '../model/types';
import { onboardingService } from '../service/onboarding.service';
import { getStepsForRole, getNextStep, isOnboardingComplete } from '../model/steps';
import { syncOnboardingCache } from '../hooks/useOnboardingStateQuery';
// Note: `state.userRole` est déjà typé `Role` via `OnboardingState`; aucun cast nécessaire.

interface OnboardingStore {
  state: OnboardingState | null;
  currentStep: OnboardingStep | null;
  allSteps: OnboardingStep[];
  isLoading: boolean;
  isVisible: boolean;
  spotlightPosition: SpotlightPosition | null;
  error: string | null;

  /**
   * Applique un état déjà chargé (par `useOnboardingStateQuery`) sans requête.
   * C'est le chemin normal : le réseau est du ressort de React Query.
   */
  hydrate: (state: OnboardingState) => void;
  initialize: () => Promise<void>;
  refresh: () => Promise<void>;

  hoverSection: (sectionId: string) => Promise<void>;
  completeStep: (stepId: string) => Promise<void>;

  nextStep: () => void;
  previousStep: () => void;
  goToStep: (stepId: string) => void;

  complete: () => Promise<void>;
  skip: () => Promise<void>;
  reset: () => Promise<void>;

  show: () => void;
  hide: () => void;
  toggle: () => void;
  /**
   * "Plus tard": hides the guide until the next page load. Unlike `hide`, a later `hydrate`
   * (cache resynced after a tutorial is finished) does not bring it back (VOL-141).
   */
  pause: () => void;
  /** New account on the same tablet: its guide must not inherit the previous user's "Plus tard". */
  clearPause: () => void;
  isPaused: boolean;

  setSpotlightPosition: (position: SpotlightPosition | null) => void;
}

/** The API does not know the client-side flags: carry over the ones already hydrated. */
function withClientFlags(fromApi: OnboardingState, current: OnboardingState | null): OnboardingState {
  return {
    ...fromApi,
    canOpenStats: fromApi.canOpenStats ?? current?.canOpenStats,
    isPhone: fromApi.isPhone ?? current?.isPhone,
  };
}

function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message;
  return fallback;
}

export const useOnboardingController = create<OnboardingStore>((set, get) => ({
  state: null,
  currentStep: null,
  allSteps: [],
  isLoading: false,
  isVisible: false,
  spotlightPosition: null,
  error: null,
  isPaused: false,

  hydrate: (incoming: OnboardingState) => {
    const state = withClientFlags(incoming, get().state);
    const allSteps = getStepsForRole(state.userRole, state);
    const currentStep = getNextStep(
      state.userRole,
      state,
      state.completedSteps,
      state.hoveredSections,
    );

    const shouldShowOnboarding =
      !state.isCompleted && !state.skippedAt && currentStep !== null && !get().isPaused;

    set({
      state,
      allSteps,
      currentStep,
      isVisible: shouldShowOnboarding,
      isLoading: false,
      error: null,
    });
  },

  initialize: async () => {
    set({ isLoading: true, error: null });
    try {
      const state = await onboardingService.getState();
      syncOnboardingCache(state);
      get().hydrate(state);
    } catch (error) {
      set({
        error: toErrorMessage(error, 'Erreur inconnue'),
        isLoading: false,
      });
    }
  },

  refresh: async () => {
    const { initialize } = get();
    await initialize();
  },

  hoverSection: async (sectionId: string) => {
    const { state } = get();
    if (!state || state.hoveredSections.includes(sectionId)) return;

    try {
      const newState = withClientFlags(await onboardingService.markSectionHovered(sectionId), get().state);
      syncOnboardingCache(newState);
      const allSteps = getStepsForRole(newState.userRole, newState);
      const currentStep = getNextStep(
        newState.userRole,
        newState,
        newState.completedSteps,
        newState.hoveredSections,
      );

      set({ state: newState, allSteps, currentStep, error: null });
    } catch (error) {
      set({ error: toErrorMessage(error, 'Erreur marquage section') });
    }
  },

  completeStep: async (stepId: string) => {
    const { state } = get();
    if (!state) return;

    try {
      const newState = withClientFlags(await onboardingService.completeStep(stepId), get().state);
      syncOnboardingCache(newState);
      const allSteps = getStepsForRole(newState.userRole, newState);
      const nextCurrentStep = getNextStep(
        newState.userRole,
        newState,
        newState.completedSteps,
        newState.hoveredSections,
      );

      const isComplete = isOnboardingComplete(
        newState.userRole,
        newState,
        newState.completedSteps,
        newState.hoveredSections,
      );

      set({
        state: newState,
        allSteps,
        currentStep: nextCurrentStep,
        isVisible: !isComplete,
        error: null,
      });
    } catch (error) {
      set({ error: toErrorMessage(error, 'Erreur complétion étape') });
    }
  },

  nextStep: () => {
    const { state, currentStep, allSteps } = get();
    if (!state || !currentStep) return;

    const currentIndex = allSteps.findIndex((s) => s.id === currentStep.id);
    if (currentIndex < allSteps.length - 1) {
      set({ currentStep: allSteps[currentIndex + 1] });
    }
  },

  previousStep: () => {
    const { currentStep, allSteps } = get();
    if (!currentStep) return;

    const currentIndex = allSteps.findIndex((s) => s.id === currentStep.id);
    if (currentIndex > 0) {
      set({ currentStep: allSteps[currentIndex - 1] });
    }
  },

  goToStep: (stepId: string) => {
    const { allSteps } = get();
    const step = allSteps.find((s) => s.id === stepId);
    if (step) {
      set({ currentStep: step });
    }
  },

  complete: async () => {
    const { state } = get();
    if (!state) return;

    try {
      const newState = withClientFlags(await onboardingService.complete(), get().state);
      syncOnboardingCache(newState);
      set({ state: newState, isVisible: false, error: null });
    } catch (error) {
      set({ error: toErrorMessage(error, 'Erreur lors de la complétion') });
    }
  },

  skip: async () => {
    try {
      const newState = withClientFlags(await onboardingService.skip(), get().state);
      syncOnboardingCache(newState);
      set({ state: newState, isVisible: false, error: null });
    } catch (error) {
      set({ error: toErrorMessage(error, 'Erreur skip onboarding') });
    }
  },

  reset: async () => {
    try {
      const newState = withClientFlags(await onboardingService.reset(), get().state);
      syncOnboardingCache(newState);
      const allSteps = getStepsForRole(newState.userRole, newState);
      const currentStep = getNextStep(
        newState.userRole,
        newState,
        newState.completedSteps,
        newState.hoveredSections,
      );

      set({
        state: newState,
        allSteps,
        currentStep,
        isVisible: true,
        isPaused: false,
        error: null,
      });
    } catch (error) {
      set({ error: toErrorMessage(error, 'Erreur reset onboarding') });
    }
  },

  show: () => set({ isVisible: true, isPaused: false }),
  pause: () => set({ isVisible: false, isPaused: true }),
  clearPause: () => set({ isPaused: false }),
  hide: () => set({ isVisible: false }),
  toggle: () => set((s) => ({ isVisible: !s.isVisible })),

  setSpotlightPosition: (position) => set({ spotlightPosition: position }),
}));
